import { http } from "./http";

function getNotificationRecipient(_user) {
  return {
    recipientType: "room",
    recipientId: "admins",
  };
}

export const NotificationsService = {
  getRecipient(user) {
    return getNotificationRecipient(user);
  },

  async list(user, params = {}) {
    const recipient = getNotificationRecipient(user);

    return http("/api/notifications", {
      method: "GET",
      params: {
        recipientType: recipient.recipientType,
        recipientId: recipient.recipientId,
        limit: 20,
        offset: 0,
        ...params,
      },
    });
  },

  async markRead(id, user) {
    const recipient = getNotificationRecipient(user);

    return http(`/api/notifications/${id}/read`, {
      method: "PATCH",
      body: {
        recipientType: recipient.recipientType,
        recipientId: recipient.recipientId,
      },
    });
  },

  async markAllRead(user) {
    const recipient = getNotificationRecipient(user);

    return http(`/api/notifications/read-all`, {
      method: "PATCH",
      body: {
        recipientType: recipient.recipientType,
        recipientId: recipient.recipientId,
      },
    });
  },
};

const ADMIN_ROOT = "/admin/dashboard";
const FOOD_ROOT = "/admin/food-dashboard";

function clean(value) {
  return String(value || "").trim();
}

function safeLower(value) {
  return clean(value).toLowerCase();
}

function getActionUrl(item = {}) {
  return clean(
    item?.actionUrl ||
      item?.action_url ||
      item?.url ||
      item?.link ||
      item?.path ||
      item?.redirectUrl ||
      item?.redirect_url ||
      item?.deepLink ||
      item?.deep_link ||
      item?.meta?.actionUrl ||
      item?.meta?.action_url ||
      item?.meta?.url ||
      item?.meta?.link ||
      item?.meta?.path ||
      ""
  );
}

function normalizePath(rawUrl) {
  const raw = clean(rawUrl);
  if (!raw) return "";

  if (raw.startsWith("/")) return raw;

  try {
    const base = typeof window !== "undefined" ? window.location.origin : "http://localhost";
    const parsed = new URL(raw, base);
    return `${parsed.pathname}${parsed.search || ""}${parsed.hash || ""}`;
  } catch (_err) {
    return raw;
  }
}

function pathWithoutQuery(path) {
  return clean(path).split("?")[0].split("#")[0];
}

function lastSegmentAfter(path, marker) {
  const cleanPath = pathWithoutQuery(path);
  const index = cleanPath.indexOf(marker);
  if (index === -1) return "";

  const rest = cleanPath.slice(index + marker.length);
  const [id] = rest.split("/").filter(Boolean);
  return clean(id);
}

function route(path) {
  return `${ADMIN_ROOT}${path}`;
}

function idRoute(basePath, id) {
  return id ? route(`${basePath}/${encodeURIComponent(id)}`) : route(basePath);
}

function isDeleteEvent(key) {
  return /\b(delete|deleted|removed|remove)\b/i.test(key);
}

function buildSearchKey(item = {}, actionPath = "") {
  const meta = item?.meta && typeof item.meta === "object" ? item.meta : {};

  return [
    actionPath,
    item?.type,
    item?.event_type,
    item?.eventType,
    item?.title,
    item?.message,
    item?.body,
    item?.description,
    meta?.type,
    meta?.event_type,
    meta?.eventType,
    meta?.entityType,
    meta?.entity_type,
    meta?.module,
    meta?.action,
    meta?.actionType,
    meta?.action_type,
    meta?.displayAction,
    meta?.actorName,
    meta?.actorEmail,
    meta?.orderId,
    meta?.order_id,
    meta?.employeeId,
    meta?.employee_id,
    meta?.partnerId,
    meta?.partner_id,
    meta?.warehouseId,
    meta?.warehouse_id,
  ]
    .map((value) => (typeof value === "object" ? JSON.stringify(value) : clean(value)))
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function mapKnownFrontendRoute(actionPath) {
  const path = normalizePath(actionPath);
  const lowerPath = safeLower(path);

  if (!path) return "";

  if (lowerPath.startsWith(`${ADMIN_ROOT}/`) || lowerPath === ADMIN_ROOT) {
    return path;
  }

  if (lowerPath.startsWith(`${FOOD_ROOT}/`) || lowerPath === FOOD_ROOT) {
    return path;
  }

  if (lowerPath.startsWith("/dashboard/")) {
    return `/admin${path}`;
  }

  return "";
}

function mapApiUrlToRoute(actionPath, key) {
  const path = normalizePath(actionPath);
  const lowerPath = safeLower(path);
  const deleted = isDeleteEvent(key);

  if (!lowerPath) return "";

  // Food admin routes
  if (lowerPath.includes("/api/restaurant") || lowerPath.includes("/api/food")) {
    if (lowerPath.includes("/orders")) return `${FOOD_ROOT}/orders`;
    if (lowerPath.includes("/employees")) return `${FOOD_ROOT}/employees`;
    if (lowerPath.includes("/items") || lowerPath.includes("/products") || lowerPath.includes("/food-items")) return `${FOOD_ROOT}/items`;
    if (lowerPath.includes("/approvals")) return `${FOOD_ROOT}/approvals`;
    if (lowerPath.includes("/reports")) return `${FOOD_ROOT}/reports`;
    if (lowerPath.includes("/settings")) return `${FOOD_ROOT}/settings`;
    return FOOD_ROOT;
  }

  // Warehouse/order routes that can arrive in admin notifications too.
  if (
    lowerPath.includes("/warehouse/customer-orders") ||
    lowerPath.includes("/api/warehouse/customer-orders") ||
    lowerPath.includes("/api/warehouse/orders") ||
    lowerPath.includes("/api/orders") ||
    lowerPath.includes("/orders/")
  ) {
    return route("/customer-orders");
  }

  if (lowerPath.includes("/warehouse/inbound-receipts") || lowerPath.includes("/api/warehouse/inbound")) {
    return route("/inbound-receipts");
  }

  if (lowerPath.includes("/warehouse/stock") || lowerPath.includes("/api/warehouse/stock")) {
    return route("/stock");
  }

  if (lowerPath.includes("/api/warehouse/inventory") || lowerPath.includes("/warehouse/inventory")) {
    return route("/inventory");
  }

  // Admin routes
  if (lowerPath.includes("/api/admin/delivery-partners")) {
    const id = lastSegmentAfter(path, "/api/admin/delivery-partners/");
    return deleted ? route("/delivery-partners") : idRoute("/delivery-partners", id);
  }

  if (lowerPath.includes("/api/admin/employees")) {
    const id = lastSegmentAfter(path, "/api/admin/employees/");
    return deleted ? route("/employees") : idRoute("/employees", id);
  }

  if (lowerPath.includes("/api/admin/warehouse-employees")) {
    return route("/employees");
  }

  if (lowerPath.includes("/api/admin/warehouse") || lowerPath.includes("/api/admin/warehouses")) {
    const id =
      lastSegmentAfter(path, "/api/admin/warehouses/") ||
      lastSegmentAfter(path, "/api/admin/warehouse/");
    return deleted ? route("/warehouse") : idRoute("/warehouse", id);
  }

  if (lowerPath.includes("/api/admin/products") || lowerPath.includes("/api/admin/catalog")) {
    const id = lastSegmentAfter(path, "/api/admin/products/");
    return deleted ? route("/products") : idRoute("/products", id);
  }

  if (lowerPath.includes("/api/admin/inventory")) return route("/inventory");
  if (lowerPath.includes("/api/admin/stock")) return route("/stock");

  if (lowerPath.includes("/api/admin/users")) {
    const id = lastSegmentAfter(path, "/api/admin/users/");
    return deleted ? route("/users") : idRoute("/users", id);
  }

  if (lowerPath.includes("/api/admin/roles")) return route("/roles");
  if (lowerPath.includes("/api/admin/email-templates")) return route("/email-templates");
  if (lowerPath.includes("/api/admin/settings")) return route("/settings");
  if (lowerPath.includes("/api/admin/posters")) return route("/posters");
  if (lowerPath.includes("/api/admin/broadcast")) return route("/broadcast-messages");
  if (lowerPath.includes("/api/admin/driver-wallet")) return route("/driver-wallet");
  if (lowerPath.includes("/api/admin/user-notifications")) return route("/user-notifications");
  if (lowerPath.includes("/api/admin/app-layout")) return route("/app-layout");
  if (lowerPath.includes("/api/admin/reports")) return route("/reports");

  return "";
}

function mapTextToRoute(key) {
  const text = safeLower(key);
  if (!text) return "";

  // Keep more specific routes above generic words like warehouse/user/product.
  if (text.includes("food order") || text.includes("restaurant order")) return `${FOOD_ROOT}/orders`;
  if (text.includes("restaurant") || text.includes("food dashboard")) return FOOD_ROOT;
  if (text.includes("food item") || text.includes("menu item")) return `${FOOD_ROOT}/items`;

  if (
    text.includes("warehouse order") ||
    text.includes("customer order") ||
    text.includes("order created") ||
    text.includes("order assigned") ||
    text.includes("order cancelled") ||
    text.includes("order delivered") ||
    text.includes("dispatch") ||
    /\bwo[-_\s]?\d+/i.test(text)
  ) {
    return route("/customer-orders");
  }

  if (text.includes("inbound") || text.includes("receipt") || text.includes("putaway") || text.includes("grn")) {
    return route("/inbound-receipts");
  }

  if (text.includes("driver wallet") || text.includes("wallet")) return route("/driver-wallet");

  if (
    text.includes("delivery partner") ||
    text.includes("driver inactive") ||
    text.includes("driver active") ||
    text.includes("driver deleted") ||
    text.includes("partner registration") ||
    text.includes("partner deleted") ||
    text.includes("onboarding fields") ||
    /\bdp[-_\s]?\d+/i.test(text)
  ) {
    return route("/delivery-partners");
  }

  if (
    text.includes("employee") ||
    text.includes("admin employee") ||
    text.includes("approval pending") ||
    text.includes("onboarding pending") ||
    /\bemp[-_\s]?\d+/i.test(text)
  ) {
    return route("/employees");
  }

  if (text.includes("low stock") || text.includes("stock") || text.includes("expiry") || text.includes("expired")) {
    return route("/stock");
  }

  if (text.includes("inventory") || text.includes("batch")) return route("/inventory");
  if (text.includes("purchase") && text.includes("history")) return route("/inventory/purchase-history");

  if (text.includes("app layout") || text.includes("category order") || text.includes("layout")) return route("/app-layout");
  if (text.includes("broadcast")) return route("/broadcast-messages");
  if (text.includes("poster")) return route("/posters");
  if (text.includes("email template")) return route("/email-templates");
  if (text.includes("setting")) return route("/settings");
  if (text.includes("role") || text.includes("permission")) return route("/roles");
  if (text.includes("report") || text.includes("analytics")) return route("/reports");
  if (text.includes("pricing") || text.includes("price")) return route("/pricing");

  if (text.includes("product") || text.includes("catalog") || text.includes("category") || text.includes("subcategory")) {
    return route("/products");
  }

  if (text.includes("warehouse")) return route("/warehouse");
  if (text.includes("customer") || text.includes("user registered") || text.includes("user deleted") || text.includes("user")) {
    return route("/users");
  }

  return "";
}

function routeWithoutQuery(path) {
  return pathWithoutQuery(normalizePath(path)).toLowerCase();
}

export function isWarehousePanelNotification(item = {}, mappedRoute = "") {
  const actionPath = normalizePath(getActionUrl(item));
  const key = buildSearchKey(item, actionPath);
  const text = safeLower(key);
  const lowerAction = safeLower(actionPath);
  const lowerRoute = routeWithoutQuery(mappedRoute || mapNotificationToRoute(item));

  // Strong URL/API signal: this notification was produced by warehouse/order modules.
  if (
    lowerAction.includes("/api/warehouse/") ||
    lowerAction.includes("/warehouse/customer-orders") ||
    lowerAction.includes("/warehouse/inbound-receipts") ||
    lowerAction.includes("/warehouse/stock") ||
    lowerAction.includes("/warehouse/inventory") ||
    lowerAction.includes("/warehouse/products") ||
    lowerAction.includes("/warehouse/activity")
  ) {
    return true;
  }

  // Routes that exist for warehouse operations in this app.
  if (
    lowerRoute === `${ADMIN_ROOT}/customer-orders` ||
    lowerRoute.startsWith(`${ADMIN_ROOT}/customer-orders/`) ||
    lowerRoute === `${ADMIN_ROOT}/inbound-receipts` ||
    lowerRoute.startsWith(`${ADMIN_ROOT}/inbound-receipts/`) ||
    lowerRoute === `${ADMIN_ROOT}/stock` ||
    lowerRoute.startsWith(`${ADMIN_ROOT}/stock/`)
  ) {
    return true;
  }

  // Text fallback. Keep this specific; generic words like "warehouse" or "product" alone
  // are not enough because admin warehouse/product notifications also exist.
  if (
    text.includes("warehouse order") ||
    text.includes("customer order") ||
    text.includes("order assigned") ||
    text.includes("order delivered") ||
    text.includes("order cancelled") ||
    text.includes("dispatch") ||
    text.includes("inbound") ||
    text.includes("receipt") ||
    text.includes("putaway") ||
    text.includes("grn") ||
    text.includes("low stock") ||
    text.includes("expiry") ||
    text.includes("expired") ||
    text.includes("stock alert") ||
    text.includes("warehouse inventory") ||
    text.includes("inventory batch") ||
    /\bwo[-_\s]?\d+/i.test(text)
  ) {
    return true;
  }

  return false;
}


export function isFoodPanelNotification(item = {}, mappedRoute = "") {
  const actionPath = normalizePath(getActionUrl(item));
  const key = buildSearchKey(item, actionPath);
  const text = safeLower(key);
  const lowerAction = safeLower(actionPath);
  const lowerRoute = routeWithoutQuery(mappedRoute || mapNotificationToRoute(item));
  const meta = item?.meta && typeof item.meta === "object" ? item.meta : {};

  if (
    lowerRoute === FOOD_ROOT ||
    lowerRoute.startsWith(`${FOOD_ROOT}/`) ||
    lowerAction.includes("/admin/food-dashboard") ||
    lowerAction.includes("/api/restaurant-orders") ||
    lowerAction.includes("/api/restaurant-app") ||
    lowerAction.includes("/api/restaurant-auth/admin/restaurant")
  ) {
    return true;
  }

  const metaModule = safeLower(meta.module || meta.dashboard || meta.sourceService || meta.source_service);
  if (
    metaModule.includes("food") ||
    metaModule.includes("restaurant-app-service") ||
    metaModule.includes("restaurant_order") ||
    metaModule.includes("food-dashboard")
  ) {
    return true;
  }

  if (isWarehousePanelNotification(item, mappedRoute) && !text.includes("food") && !text.includes("restaurant")) return false;

  if (
    text.includes("food order") ||
    text.includes("restaurant order") ||
    text.includes("food dashboard") ||
    text.includes("food item") ||
    text.includes("food master") ||
    text.includes("menu item") ||
    text.includes("combo request") ||
    text.includes("restaurant profile") ||
    text.includes("restaurant availability") ||
    text.includes("restaurant employee") ||
    text.includes("restaurant created") ||
    text.includes("restaurant updated") ||
    text.includes("restaurant live") ||
    text.includes("food approval") ||
    text.includes("new combo") ||
    text.includes("restaurant_food") ||
    text.includes("restaurant_combo") ||
    text.includes("restaurant_order")
  ) {
    return true;
  }

  return false;
}

function uniqueById(items = []) {
  const seen = new Set();
  const out = [];
  for (const item of items) {
    const key = item?.id || `${item?.type || ""}-${item?.created_at || item?.createdAt || ""}-${item?.title || ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

export const FoodNotificationsService = {
  async list(user, params = {}) {
    const res = await NotificationsService.list(user, {
      limit: Math.max(Number(params.limit || 100), 100),
      offset: params.offset || 0,
      ...params,
    });
    const sourceItems = Array.isArray(res?.data) ? res.data : Array.isArray(res?.items) ? res.items : [];
    const filtered = uniqueById(sourceItems.filter((item) => isFoodPanelNotification(item)));
    return { ...res, data: filtered, items: filtered };
  },

  markRead(id, user) {
    return NotificationsService.markRead(id, user);
  },

  async markAllRead(user, items = []) {
    const unread = items.filter((item) => item?.id && !(item?.is_read || item?.isRead));
    await Promise.allSettled(unread.map((item) => NotificationsService.markRead(item.id, user)));
    return { success: true, updated: unread.length };
  },
};

export function mapNotificationToRoute(item) {
  const actionPath = normalizePath(getActionUrl(item));
  const key = buildSearchKey(item, actionPath);

  return (
    mapKnownFrontendRoute(actionPath) ||
    mapApiUrlToRoute(actionPath, key) ||
    mapTextToRoute(key) ||
    ADMIN_ROOT
  );
}
